import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRightLeft, CalendarDays, ChevronLeft, ChevronRight, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { Transaction, Student, Book, BookCopy, MasterItem, ColumnFilterOption } from "@/types";
import { formatGridDate, todayInputDate, addDaysInputDate, toInputDate, getPeriodRange, shiftPeriodRange, formatPeriodRangeLabel, type PeriodMode } from "@/lib/utils";

function defaultIssueForm() {
  const issue_date = todayInputDate();
  return { issue_date, due_date: addDaysInputDate(issue_date, 14) };
}

function mergeOptions(base: ColumnFilterOption[], fallback: ColumnFilterOption[]) {
  const seen = new Set<string>();
  const merged: ColumnFilterOption[] = [];
  for (const option of [...base, ...fallback]) {
    if (!option?.value || seen.has(option.value)) continue;
    seen.add(option.value);
    merged.push(option);
  }
  return merged;
}

export default function IssueRegisterPage() {
  const { api, user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";

  // Issue dialog state
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [showIssueDates, setShowIssueDates] = useState(false);
  const [issueClassId, setIssueClassId] = useState("");
  const [issueSectionId, setIssueSectionId] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedStudentIssuedBooks, setSelectedStudentIssuedBooks] = useState<Transaction[]>([]);
  const [copies, setCopies] = useState<BookCopy[]>([]);
  const [selectedCopy, setSelectedCopy] = useState("");
  const [bookSearch, setBookSearch] = useState("");
  const [books, setBooks] = useState<Book[]>([]);
  const [matchingCopies, setMatchingCopies] = useState<BookCopy[]>([]);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const copyMatchedBookIds = new Set(matchingCopies.map((copy) => String(copy.book_id)));
  const visibleBooks = books.filter((book) => !copyMatchedBookIds.has(String(book.id)));
  const [issueForm, setIssueForm] = useState(defaultIssueForm);

  // Return dialog state (per-row return)
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returningTxn, setReturningTxn] = useState<Transaction | null>(null);
  const [returnDate, setReturnDate] = useState(todayInputDate());
  const [showReturnDate, setShowReturnDate] = useState(false);

  // Detail / edit dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailTxn, setDetailTxn] = useState<Transaction | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTxn, setEditingTxn] = useState<Transaction | null>(null);
  const [editForm, setEditForm] = useState({ issue_date: "", due_date: "", return_date: "" });

  // Column filter state
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const [periodMode, setPeriodMode] = useState<PeriodMode>("week");
  const [periodRange, setPeriodRange] = useState(() => getPeriodRange("week"));
  const [classes, setClasses] = useState<MasterItem[]>([]);
  const [sections, setSections] = useState<MasterItem[]>([]);
  const [studentFilterOptions, setStudentFilterOptions] = useState<ColumnFilterOption[]>([]);
  const [bookFilterOptions, setBookFilterOptions] = useState<ColumnFilterOption[]>([]);

  const studentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bookTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const studentSearchRef = useRef<HTMLInputElement>(null);
  const bookSearchRef = useRef<HTMLInputElement>(null);
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [txnRes, studentsRes, booksRes] = await Promise.all([
        api.get("/transactions/filter-options"),
        api.get("/students", { params: { limit: 100 } }),
        api.get("/books", { params: { limit: 100 } }),
      ]);

      const txnStudents = (txnRes.data.students || []).map((s: any) => ({ label: s.name, value: String(s.id) }));
      const txnBooks = (txnRes.data.books || []).map((b: any) => ({ label: b.title, value: String(b.id) }));
      const allStudents = (studentsRes.data.items || []).map((s: any) => ({ label: s.name, value: String(s.id) }));
      const allBooks = (booksRes.data.items || []).map((b: any) => ({ label: b.title, value: String(b.id) }));

      setStudentFilterOptions(mergeOptions(txnStudents, allStudents));
      setBookFilterOptions(mergeOptions(txnBooks, allBooks));
    } catch {
      const studentFallback = transactions
        .filter((t) => !!t.student_id && !!t.student_name)
        .map((t) => ({ label: t.student_name, value: String(t.student_id) }));
      const bookFallback = transactions
        .filter((t) => !!t.book_id && !!t.book_title)
        .map((t) => ({ label: t.book_title, value: String(t.book_id) }));
      setStudentFilterOptions(mergeOptions([], studentFallback));
      setBookFilterOptions(mergeOptions([], bookFallback));
    }
  }, [api, transactions]);

  // Fetch classes/sections and filter dropdown options
  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/masters/classes/all"), api.get("/masters/sections/all")]);
        setClasses(c.data);
        setSections(s.data);
      } catch {}
      await fetchFilterOptions();
    })();
  }, [api, fetchFilterOptions]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20, status: columnFilters.status || "issued" };
      if (columnFilters.student_id) params.student_id = columnFilters.student_id;
      if (columnFilters.book_id) params.book_id = columnFilters.book_id;
      if (columnFilters.accession_no) params.search = columnFilters.accession_no;
      if (columnFilters.class_section) params.class_id = columnFilters.class_section;
      if (columnFilters.section) params.section_id = columnFilters.section;
      if (periodRange.from) params.from_date = periodRange.from;
      else if (columnFilters.issue_date_from) params.from_date = columnFilters.issue_date_from;
      if (periodRange.to) params.to_date = periodRange.to;
      else if (columnFilters.issue_date_to) params.to_date = columnFilters.issue_date_to;

      const res = await api.get("/transactions", { params });
      setTransactions(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters, periodRange]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    if (transactions.length === 0) return;
    const studentFallback = transactions
      .filter((t) => !!t.student_id && !!t.student_name)
      .map((t) => ({ label: t.student_name, value: String(t.student_id) }));
    const bookFallback = transactions
      .filter((t) => !!t.book_id && !!t.book_title)
      .map((t) => ({ label: t.book_title, value: String(t.book_id) }));

    setStudentFilterOptions((prev) => mergeOptions(prev, studentFallback));
    setBookFilterOptions((prev) => mergeOptions(prev, bookFallback));
  }, [transactions]);

  // Debounced column filter change handler
  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    const next = { ...filters };
    if (filters.class_section !== columnFilters.class_section) {
      next.section = "";
    }
    setColumnFilters(next);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, [columnFilters.class_section]);

  const handlePeriodModeChange = (mode: PeriodMode) => {
    setPeriodMode(mode);
    setPeriodRange(getPeriodRange(mode, periodRange.from || new Date()));
    setPage(1);
  };

  const shiftPeriod = (delta: number) => {
    setPeriodRange((prev) => shiftPeriodRange(periodMode, prev.from, delta));
    setPage(1);
  };

  const loadSelectedStudentIssuedBooks = useCallback(
    async (studentId: string) => {
      try {
        const res = await api.get("/transactions", { params: { student_id: studentId, status: "issued", limit: 20 } });
        setSelectedStudentIssuedBooks(res.data.items || []);
      } catch {
        setSelectedStudentIssuedBooks([]);
      }
    },
    [api],
  );

  // Issue dialog helpers
  const searchStudents = useCallback(
    async (q: string, classId = issueClassId, sectionId = issueSectionId) => {
      const hasSearch = q.length >= 2;
      const hasClassFilter = !!classId;
      if (!hasSearch && !hasClassFilter) {
        setStudents([]);
        return;
      }
      try {
        const params: Record<string, string | number> = { limit: 20, status: "active" };
        if (hasSearch) params.search = q;
        if (classId) params.class_id = classId;
        if (sectionId) params.section_id = sectionId;
        const res = await api.get("/students", { params });
        setStudents(res.data.items);
      } catch {}
    },
    [api, issueClassId, issueSectionId],
  );

  const searchBooks = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setBooks([]);
        setMatchingCopies([]);
        return;
      }
      try {
        const res = await api.get("/books", { params: { search: q, limit: 10 } });
        const matchedBooks = res.data.items || [];
        setBooks(matchedBooks);

        const normalizedQuery = q.trim().toLowerCase();
        const copyResults = await Promise.all(
          matchedBooks.map(async (book: Book) => {
            const copiesRes = await api.get(`/books/${book.id}/copies`);
            return (copiesRes.data || [])
              .filter((copy: BookCopy) => copy.status === "available")
              .filter((copy: BookCopy) => {
                const accession = String(copy.accession_no || "").toLowerCase();
                const barcode = String(copy.barcode || "").toLowerCase();
                return accession.includes(normalizedQuery) || barcode.includes(normalizedQuery);
              })
              .map((copy: BookCopy) => ({ ...copy, book_title: book.title }));
          }),
        );

        setMatchingCopies(copyResults.flat());
      } catch {}
    },
    [api],
  );

  const handleStudentSearchChange = (v: string) => {
    setStudentSearch(v);
    if (studentTimer.current) clearTimeout(studentTimer.current);
    studentTimer.current = setTimeout(() => searchStudents(v), 300);
  };

  const handleIssueClassChange = (classId: string) => {
    const resolved = classId === "__all__" ? "" : classId;
    setIssueClassId(resolved);
    setIssueSectionId("");
    setSelectedStudent(null);
    setSelectedStudentIssuedBooks([]);
    void searchStudents(studentSearch, resolved, "");
  };

  const handleIssueSectionChange = (sectionId: string) => {
    const resolved = sectionId === "__all__" ? "" : sectionId;
    setIssueSectionId(resolved);
    setSelectedStudent(null);
    setSelectedStudentIssuedBooks([]);
    void searchStudents(studentSearch, issueClassId, resolved);
  };

  const handleBookSearchChange = (v: string) => {
    setBookSearch(v);
    if (bookTimer.current) clearTimeout(bookTimer.current);
    bookTimer.current = setTimeout(() => searchBooks(v), 300);
  };

  const selectBook = async (book: Book) => {
    setSelectedBook(book);
    setSelectedCopy("");
    setMatchingCopies([]);
    try {
      const res = await api.get(`/books/${book.id}/copies`);
      setCopies(res.data.filter((c: BookCopy) => c.status === "available"));
    } catch {
      setCopies([]);
    }
  };

  const [studentActiveIndex, setStudentActiveIndex] = useState(0);
  const [bookActiveIndex, setBookActiveIndex] = useState(0);
  const bookOptionCount = matchingCopies.length + visibleBooks.length;

  useEffect(() => setStudentActiveIndex(0), [students]);
  useEffect(() => setBookActiveIndex(0), [matchingCopies, books]);
  useEffect(() => {
    document.getElementById(`issue-student-option-${studentActiveIndex}`)?.scrollIntoView?.({ block: "nearest" });
  }, [studentActiveIndex]);
  useEffect(() => {
    document.getElementById(`issue-book-option-${bookActiveIndex}`)?.scrollIntoView?.({ block: "nearest" });
  }, [bookActiveIndex]);

  const pickStudent = (s: Student) => {
    setSelectedStudent(s);
    setStudents([]);
    void loadSelectedStudentIssuedBooks(String(s.id));
  };

  const pickCopy = (copy: BookCopy) => {
    const book = books.find((candidate) => String(candidate.id) === String(copy.book_id));
    if (!book) return;
    void selectBook(book).then(() => setSelectedCopy(String(copy.id)));
    setBooks([]);
  };

  const pickBook = (b: Book) => {
    void selectBook(b);
    setBooks([]);
  };

  const pickBookOption = (index: number) => {
    if (index < matchingCopies.length) pickCopy(matchingCopies[index]);
    else if (visibleBooks[index - matchingCopies.length]) pickBook(visibleBooks[index - matchingCopies.length]);
  };

  const handleListKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    count: number,
    active: number,
    setActive: (index: number) => void,
    choose: (index: number) => void,
  ) => {
    if (count === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((active + 1) % count);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((active - 1 + count) % count);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(Math.min(active, count - 1));
    }
  };

  const changeStudent = () => {
    setSelectedStudent(null);
    setSelectedStudentIssuedBooks([]);
    void searchStudents(studentSearch);
    setTimeout(() => studentSearchRef.current?.focus(), 0);
  };

  const changeBook = () => {
    setSelectedBook(null);
    setSelectedCopy("");
    setCopies([]);
    void searchBooks(bookSearch);
    setTimeout(() => bookSearchRef.current?.focus(), 0);
  };

  const issueBook = async () => {
    if (!selectedStudent || !selectedCopy) {
      toast.error("Select a student and book copy");
      return;
    }
    if (issueForm.due_date < issueForm.issue_date) {
      toast.error("Due date cannot be before issue date");
      return;
    }
    try {
      await api.post("/transactions/issue", {
        student_id: selectedStudent.id,
        book_copy_id: selectedCopy,
        issue_date: issueForm.issue_date,
        due_date: issueForm.due_date,
      });
      toast.success("Book issued successfully");
      resetIssueForm();
      setIssueDialogOpen(false);
      await Promise.all([fetchTransactions(), fetchFilterOptions()]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Issue failed");
    }
  };

  const openReturnDialog = (txn: Transaction) => {
    setReturningTxn(txn);
    setReturnDate(todayInputDate());
    setShowReturnDate(false);
    setReturnDialogOpen(true);
  };

  const confirmReturn = async () => {
    if (!returningTxn) return;
    try {
      const res = await api.post("/transactions/return", {
        transaction_id: returningTxn.id,
        return_date: returnDate,
      });
      toast.success(`Book returned${res.data.fine_amount > 0 ? ` | Fine: ₹${res.data.fine_amount}` : ""}`);
      setReturnDialogOpen(false);
      setReturningTxn(null);
      await Promise.all([fetchTransactions(), fetchFilterOptions()]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Return failed");
    }
  };

  const resetIssueForm = () => {
    setSelectedStudent(null);
    setSelectedStudentIssuedBooks([]);
    setSelectedBook(null);
    setSelectedCopy("");
    setCopies([]);
    setStudents([]);
    setBooks([]);
    setMatchingCopies([]);
    setIssueClassId("");
    setIssueSectionId("");
    setIssueForm(defaultIssueForm());
    setStudentSearch("");
    setBookSearch("");
    setShowIssueDates(false);
  };

  const issueSectionOptions = issueClassId
    ? sections.filter((s) => String(s.class_id) === String(issueClassId))
    : sections;

  const openDetailTxn = (txn: Transaction) => {
    setDetailTxn(txn);
    setDetailDialogOpen(true);
  };

  const openEditTxn = (txn: Transaction) => {
    setEditingTxn(txn);
    setEditForm({
      issue_date: toInputDate(txn.issue_date),
      due_date: toInputDate(txn.due_date),
      return_date: toInputDate(txn.return_date),
    });
    setDetailDialogOpen(false);
    setEditDialogOpen(true);
  };

  const saveEditTxn = async () => {
    if (!editingTxn) return;
    if (editForm.due_date && editForm.issue_date && editForm.due_date < editForm.issue_date) {
      toast.error("Due date cannot be before issue date");
      return;
    }
    try {
      await api.put(`/transactions/${editingTxn.id}`, editForm);
      toast.success("Transaction updated");
      setEditDialogOpen(false);
      fetchTransactions();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deleteTxn = async (id: string) => {
    if (!window.confirm("Delete this transaction? The book copy will be restored to available if it is currently issued.")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Deleted");
      await Promise.all([fetchTransactions(), fetchFilterOptions()]);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const classOptions: ColumnFilterOption[] = classes.map((c) => ({ label: c.name, value: String(c.id) }));
  const sectionOptions: ColumnFilterOption[] = columnFilters.class_section
    ? sections
        .filter((s) => String(s.class_id) === String(columnFilters.class_section))
        .map((s) => ({ label: s.name, value: String(s.id) }))
    : sections.map((s) => ({ label: s.name, value: String(s.id) }));

  const statusOptions: ColumnFilterOption[] = [{ label: "Issued", value: "issued" }];

  const hideOnMobile = "hidden md:table-cell";

  const columns: any[] = [
    {
      accessorKey: "student_id",
      header: "Student",
      meta: { filterVariant: "select", filterOptions: studentFilterOptions, filterPlaceholder: "Student" },
      cell: ({ row }: any) => {
        const classSection = [row.original.student_class_name, row.original.student_section_name].filter(Boolean).join(" · ");
        return (
          <div className="min-w-0">
            <span className="leading-snug">{row.original.student_name}</span>
            <p className="text-xs text-muted-foreground mt-0.5 md:hidden">{classSection || "—"}</p>
          </div>
        );
      },
    },
    {
      accessorKey: "book_id",
      header: "Book",
      meta: {
        filterVariant: "select",
        filterOptions: bookFilterOptions,
        filterPlaceholder: "Book",
        columnClassName: "min-w-[160px] md:min-w-[280px] md:w-[30%]",
      },
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2 min-w-0">
          {row.original.has_cover && (
            <img
              src={`${api.defaults.baseURL}/books/${row.original.book_id}/cover`}
              alt=""
              className="hidden h-11 w-8 shrink-0 rounded object-cover sm:block"
              onError={(e: any) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <div className="min-w-0">
            <span className="min-w-0 leading-snug block">{row.original.book_title}</span>
            <p className="text-xs text-muted-foreground mt-0.5 md:hidden">
              <span className="font-mono">{row.original.accession_no}</span>
              {" · "}
              {formatGridDate(row.original.issue_date)}
            </p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "accession_no",
      header: "Accession",
      meta: { filterVariant: "text", filterPlaceholder: "Accession...", hideOnMobile: true },
      cell: ({ row }: any) => <span className="font-mono text-xs">{row.original.accession_no}</span>,
    },
    {
      accessorKey: "issue_date",
      header: "Issued",
      meta: { filterVariant: "date-range", hideOnMobile: true },
      cell: ({ row }: any) => formatGridDate(row.original.issue_date),
    },
    {
      accessorKey: "due_date",
      header: "Due",
      cell: ({ row }: any) => {
        const due = new Date(row.original.due_date);
        const isOverdue = row.original.status === "issued" && due < new Date();
        return <span className={isOverdue ? "font-medium text-red-500" : ""}>{formatGridDate(row.original.due_date)}</span>;
      },
    },
    {
      accessorKey: "return_date",
      header: "Return Date",
      meta: { columnClassName: hideOnMobile },
      cell: ({ row }: any) => formatGridDate(row.original.return_date),
    },
    {
      id: "class_section",
      header: "Class",
      meta: { filterVariant: "select", filterOptions: classOptions, filterPlaceholder: "Class", columnClassName: hideOnMobile },
      cell: ({ row }: any) =>
        row.original.student_class_name ? (
          <span className="text-xs">{row.original.student_class_name}</span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        ),
    },
    {
      id: "section",
      header: "Section",
      meta: { filterVariant: "select", filterOptions: sectionOptions, filterPlaceholder: "Section", hideOnMobile: true },
      cell: ({ row }: any) =>
        row.original.student_section_name ? (
          <span className="text-xs">{row.original.student_section_name}</span>
        ) : (
          <span className="text-muted-foreground text-xs">-</span>
        ),
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: { filterVariant: "select", filterOptions: statusOptions, filterPlaceholder: "Status", columnClassName: hideOnMobile },
      cell: ({ row }: any) =>
        row.original.status === "issued" ? (
          <Badge className="bg-[var(--beat)]/20 text-[var(--beat)] hover:bg-[var(--beat)]/20">issued</Badge>
        ) : (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">returned</Badge>
        ),
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            meta: { columnClassName: hideOnMobile },
            cell: ({ row }: any) => (
              <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
                {row.original.status === "issued" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-primary"
                    onClick={() => openReturnDialog(row.original)}
                    title="Return book"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Return
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditTxn(row.original)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteTxn(row.original.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="issue-register-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight whitespace-nowrap">
            Issue Register
          </h1>
        </div>
        {isAdmin && (
          <Button
            className="w-full sm:w-auto h-11 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => {
              resetIssueForm();
              setIssueDialogOpen(true);
            }}
          >
            <Plus className="w-5 h-5 mr-1.5 shrink-0" /> New Issue
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={transactions}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        toolbarExtra={
          <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
            <div className="inline-flex items-center rounded-md border bg-card p-0.5 shrink-0" data-testid="period-mode-selector">
              {(["week", "month", "year"] as PeriodMode[]).map((mode) => (
                <Button
                  key={mode}
                  type="button"
                  variant={periodMode === mode ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2 text-xs capitalize"
                  onClick={() => handlePeriodModeChange(mode)}
                >
                  {mode}
                </Button>
              ))}
            </div>
            <div className="inline-flex items-center gap-0.5 rounded-md border bg-card p-0.5 shrink-0" data-testid="period-range-nav">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => shiftPeriod(-1)}
                title={`Previous ${periodMode}`}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-1.5 text-xs sm:text-sm font-medium tabular-nums whitespace-nowrap text-center">
                {formatPeriodRangeLabel(periodMode, periodRange.from, periodRange.to)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => shiftPeriod(1)}
                title={`Next ${periodMode}`}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        }
        onRowClick={openDetailTxn}
        data-testid="transactions-table"
      />

      {/* Issue Dialog */}
      <Dialog
        open={issueDialogOpen}
        onOpenChange={(open) => {
          setIssueDialogOpen(open);
          if (!open) resetIssueForm();
        }}
      >
        <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-primary" /> New Issue
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4" data-testid="issue-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Class</Label>
                <Select value={issueClassId || "__all__"} onValueChange={handleIssueClassChange}>
                  <SelectTrigger data-testid="issue-class-select">
                    <SelectValue placeholder="All classes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All classes</SelectItem>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Section</Label>
                <Select
                  value={issueSectionId || "__all__"}
                  onValueChange={handleIssueSectionChange}
                  disabled={!issueClassId}
                >
                  <SelectTrigger data-testid="issue-section-select">
                    <SelectValue placeholder={issueClassId ? "All sections" : "Select class first"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All sections</SelectItem>
                    {issueSectionOptions.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                    {issueSectionOptions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">No sections for this class</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-full justify-between text-xs"
                onClick={() => setShowIssueDates((prev) => !prev)}
                data-testid="toggle-issue-dates-btn"
              >
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {showIssueDates ? "Hide dates" : "Show issue & due dates"}
                </span>
                <span className="text-muted-foreground font-normal tabular-nums">
                  {formatGridDate(issueForm.issue_date)} – {formatGridDate(issueForm.due_date)}
                </span>
              </Button>
              {showIssueDates && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5 min-w-0">
                    <Label>Issue Date</Label>
                    <Input
                      type="date"
                      value={issueForm.issue_date}
                      onChange={(e) => {
                        const issue_date = e.target.value;
                        setIssueForm((prev) => ({
                          issue_date,
                          due_date: prev.due_date < issue_date ? addDaysInputDate(issue_date, 14) : prev.due_date,
                        }));
                      }}
                      data-testid="issue-date-input"
                    />
                  </div>
                  <div className="space-y-1.5 min-w-0">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={issueForm.due_date}
                      onChange={(e) => setIssueForm((prev) => ({ ...prev, due_date: e.target.value }))}
                      data-testid="issue-due-date-input"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Search Student {issueClassId ? "(optional)" : "(type 2+ letters)"}</Label>
              {!selectedStudent && (
                <Input
                  ref={studentSearchRef}
                  value={studentSearch}
                  onChange={(e) => handleStudentSearchChange(e.target.value)}
                  onKeyDown={(e) =>
                    handleListKeyDown(e, students.length, studentActiveIndex, setStudentActiveIndex, (i) => pickStudent(students[i]))
                  }
                  placeholder="Name or Admission No"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={students.length > 0}
                  aria-controls="issue-student-options"
                  aria-activedescendant={students.length > 0 ? `issue-student-option-${studentActiveIndex}` : undefined}
                  data-testid="issue-student-search"
                />
              )}
              {students.length > 0 && !selectedStudent && (
                <div id="issue-student-options" role="listbox" className="border rounded-md bg-card max-h-44 overflow-y-auto">
                  {students.map((s, i) => (
                    <button
                      key={s.id}
                      id={`issue-student-option-${i}`}
                      type="button"
                      role="option"
                      aria-selected={i === studentActiveIndex}
                      tabIndex={-1}
                      onClick={() => pickStudent(s)}
                      onMouseEnter={() => setStudentActiveIndex(i)}
                      className={`w-full text-left px-3 py-2 text-sm border-b last:border-0${i === studentActiveIndex ? " bg-primary/25 shadow-[inset_3px_0_0_hsl(var(--primary))]" : ""}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{s.name}</span>
                        <span className="text-muted-foreground font-mono text-xs">{s.admission_no}</span>
                        <span className="text-muted-foreground text-xs ml-auto">
                          {s.issued_count || 0}/{s.issue_limit || 3} issued
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{[s.class_name, s.section_name].filter(Boolean).join(" - ") || "Class/Section not set"}</div>
                    </button>
                  ))}
                </div>
              )}
              {selectedStudent && (
                <div
                  role="button"
                  tabIndex={0}
                  title="Click to change student"
                  onClick={changeStudent}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      changeStudent();
                    }
                  }}
                  className="p-2 bg-primary/5 rounded-md space-y-2 cursor-pointer hover:bg-primary/10 transition-colors"
                  data-testid="issue-selected-student"
                >
                  <div className="flex items-center gap-2">
                    <Badge className="bg-primary text-white hover:bg-primary">{selectedStudent.name}</Badge>
                    <span className="font-mono text-xs">{selectedStudent.admission_no}</span>
                    <span className="text-xs text-muted-foreground">{[selectedStudent.class_name, selectedStudent.section_name].filter(Boolean).join(" - ")}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        changeStudent();
                      }}
                      className="ml-auto h-7 text-xs"
                    >
                      Change
                    </Button>
                  </div>
                  <div className="text-xs">
                    <span className="font-medium">Already issued books:</span>{" "}
                    {selectedStudentIssuedBooks.length > 0
                      ? selectedStudentIssuedBooks.map((t) => `${t.book_title} (${t.accession_no})`).join(", ")
                      : "None"}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Search Book (type 2+ letters)</Label>
              {!selectedBook && (
                <Input
                  ref={bookSearchRef}
                  value={bookSearch}
                  onChange={(e) => handleBookSearchChange(e.target.value)}
                  onKeyDown={(e) => handleListKeyDown(e, bookOptionCount, bookActiveIndex, setBookActiveIndex, pickBookOption)}
                  placeholder="Title, ISBN, accession, or barcode"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={bookOptionCount > 0}
                  aria-controls="issue-book-options"
                  aria-activedescendant={bookOptionCount > 0 ? `issue-book-option-${bookActiveIndex}` : undefined}
                  data-testid="issue-book-search"
                />
              )}
              {bookOptionCount > 0 && !selectedBook && (
                <div id="issue-book-options" role="listbox" className="space-y-2">
                  {matchingCopies.length > 0 && (
                    <div className="border rounded-md bg-card max-h-44 overflow-y-auto">
                      {matchingCopies.map((copy, i) => (
                        <button
                          key={`copy-${copy.id}`}
                          id={`issue-book-option-${i}`}
                          type="button"
                          role="option"
                          aria-selected={i === bookActiveIndex}
                          tabIndex={-1}
                          onClick={() => pickCopy(copy)}
                          onMouseEnter={() => setBookActiveIndex(i)}
                          className={`w-full text-left px-3 py-2 text-sm border-b last:border-0${i === bookActiveIndex ? " bg-primary/25 shadow-[inset_3px_0_0_hsl(var(--primary))]" : ""}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{copy.book_title}</span>
                            <span className="font-mono text-xs">{copy.accession_no}</span>
                            {copy.barcode && <span className="text-xs text-muted-foreground">{copy.barcode}</span>}
                            <Badge variant="outline" className="ml-auto">
                              available
                            </Badge>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {visibleBooks.length > 0 && (
                    <div className="border rounded-md bg-card max-h-44 overflow-y-auto">
                      {visibleBooks.map((b, j) => {
                        const i = matchingCopies.length + j;
                        return (
                          <button
                            key={b.id}
                            id={`issue-book-option-${i}`}
                            type="button"
                            role="option"
                            aria-selected={i === bookActiveIndex}
                            tabIndex={-1}
                            onClick={() => pickBook(b)}
                            onMouseEnter={() => setBookActiveIndex(i)}
                            className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 flex items-center gap-2${i === bookActiveIndex ? " bg-primary/25 shadow-[inset_3px_0_0_hsl(var(--primary))]" : ""}`}
                          >
                            <img
                              src={`${api.defaults.baseURL}/books/${b.id}/cover`}
                              alt=""
                              className="w-8 h-11 object-cover rounded"
                              onError={(e: any) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <div className="min-w-0">
                              <p className="font-medium truncate">{b.title}</p>
                              <p className="text-xs text-muted-foreground">ISBN: {b.isbn || "-"}</p>
                            </div>
                            <Badge variant="outline" className="ml-auto">
                              {b.available_copies} available
                            </Badge>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {selectedBook && (
                <div className="p-2 rounded-md border bg-muted/40 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      title="Click to change book"
                      onClick={changeBook}
                      className="min-w-0 text-left rounded hover:opacity-80"
                      data-testid="issue-selected-book"
                    >
                      <p className="text-sm font-medium">{selectedBook.title}</p>
                      <p className="text-xs text-muted-foreground">ISBN: {selectedBook.isbn || "-"}</p>
                      <p className="text-xs text-muted-foreground">Authors: {(selectedBook.authors || []).join(", ") || "-"}</p>
                    </button>
                    <Badge variant="outline">{copies.length} copies available</Badge>
                  </div>

                  <Select value={selectedCopy} onValueChange={setSelectedCopy}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select copy" />
                    </SelectTrigger>
                    <SelectContent>
                      {copies.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.accession_no} ({c.barcode || c.accession_no})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={changeBook}>
                      Change Book
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
            <Button variant="outline" onClick={() => setIssueDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={issueBook}
              disabled={!selectedStudent || !selectedCopy}
              data-testid="issue-submit-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Issue Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Confirm Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="max-w-md max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary" /> Return Book
            </DialogTitle>
          </DialogHeader>
          {returningTxn && (
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
              <div className="p-2 bg-muted rounded text-sm space-y-1">
                <p>
                  <span className="font-medium">{returningTxn.book_title}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {returningTxn.student_name} · {returningTxn.accession_no} · Due {formatGridDate(returningTxn.due_date)}
                </p>
              </div>
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-full justify-between text-xs"
                  onClick={() => setShowReturnDate((prev) => !prev)}
                  data-testid="toggle-return-date-btn"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    {showReturnDate ? "Hide return date" : "Show return date"}
                  </span>
                  <span className="text-muted-foreground font-normal tabular-nums">{formatGridDate(returnDate)}</span>
                </Button>
                {showReturnDate && (
                  <div className="space-y-1.5 min-w-0">
                    <Label>Return Date</Label>
                    <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} data-testid="return-date-input" />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={confirmReturn} data-testid="confirm-return-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Return Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-md max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Issue Details</DialogTitle>
          </DialogHeader>
          {detailTxn && (
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4" data-testid="issue-detail-dialog">
              <div className="flex items-start gap-3">
                {detailTxn.has_cover && (
                  <img
                    src={`${api.defaults.baseURL}/books/${detailTxn.book_id}/cover`}
                    alt=""
                    className="w-14 h-20 shrink-0 object-cover rounded"
                    onError={(e: any) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <div className="min-w-0 space-y-1">
                  <p className="font-medium leading-snug">{detailTxn.book_title}</p>
                  <p className="text-sm text-muted-foreground font-mono">{detailTxn.accession_no}</p>
                  {detailTxn.status === "issued" ? (
                    <Badge className="bg-[var(--beat)]/20 text-[var(--beat)] hover:bg-[var(--beat)]/20">issued</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">returned</Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Student</p>
                  <p className="font-medium">{detailTxn.student_name}</p>
                  {detailTxn.student_admission_no && (
                    <p className="text-xs text-muted-foreground font-mono">{detailTxn.student_admission_no}</p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Class / Section</p>
                  <p className="font-medium">
                    {[detailTxn.student_class_name, detailTxn.student_section_name].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Issue Date</p>
                  <p className="font-medium">{formatGridDate(detailTxn.issue_date)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Due Date</p>
                  <p className="font-medium">{formatGridDate(detailTxn.due_date)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Return Date</p>
                  <p className="font-medium">{formatGridDate(detailTxn.return_date)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Fine</p>
                  <p className={`font-medium ${detailTxn.fine_amount > 0 ? "text-red-600" : "text-green-600"}`}>
                    ₹{detailTxn.fine_amount || 0}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0 !flex-row !flex-nowrap justify-end gap-2 space-x-0">
            <Button
              className="flex-1 sm:flex-none h-11 text-base font-semibold bg-slate-200 text-slate-800 hover:bg-slate-300"
              onClick={() => setDetailDialogOpen(false)}
            >
              Close
            </Button>
            {isAdmin && detailTxn && (
              <>
                <Button
                  className="flex-1 sm:flex-none h-11 text-base font-semibold bg-[var(--beat)] text-white hover:bg-[var(--beat)]/90"
                  onClick={() => openEditTxn(detailTxn)}
                >
                  <Pencil className="w-5 h-5 mr-1.5" /> Edit
                </Button>
                {detailTxn.status === "issued" && (
                  <Button
                    className="flex-1 sm:flex-none h-11 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openReturnDialog(detailTxn);
                    }}
                  >
                    <RotateCcw className="w-5 h-5 mr-1.5" /> Return
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Edit Transaction</DialogTitle>
          </DialogHeader>
          {editingTxn && (
            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
              <div className="p-2 bg-muted rounded text-sm">
                <span className="font-medium">{editingTxn.book_title}</span> - {editingTxn.student_name}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Issue Date</Label>
                  <Input type="date" value={editForm.issue_date} onChange={(e) => setEditForm({ ...editForm, issue_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Due Date</Label>
                  <Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
                </div>
                {editingTxn.status === "returned" && (
                  <div className="space-y-1.5">
                    <Label>Return Date</Label>
                    <Input type="date" value={editForm.return_date} onChange={(e) => setEditForm({ ...editForm, return_date: e.target.value })} />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={saveEditTxn}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
